/** Facebook rules — what Tom is looking for, in his words, saved to the CRM.
 *
 * Two modes, because he works two completely different funnels on Facebook:
 * DEALS (sellers, wholesalers, FSBO posts) and CONTRACTORS (trades to hire).
 * A post that's gold in one is noise in the other, so each mode carries its own
 * qualifying rules, disqualifiers, voice and canned answers. The model reads
 * these instead of guessing what Tom wants.
 *
 * WHERE THEY LIVE: nested under the AUTOMATION settings key (as
 * `AUTOMATION.fbRules`), not a key of their own. The write door's whitelist
 * (WRITABLE_SETTING_KEYS, PHASE17) accepts AUTOMATION today, so this ships with
 * no migration and syncs to every device Tom logs into. Settings writes replace
 * the key WHOLE, so always merge through `automationWithRules` — never post a
 * bare {fbRules} object or the org's other automation toggles are gone.
 *
 * Nothing here logs into Facebook or sends anything. Rules decide what the
 * model says and what gets flagged; Tom still posts by hand. */

export type FbMode = "deals" | "contractors";

export interface ModeRules {
  /** free text: what a good one looks like, in Tom's words — goes to the model */
  looksLike: string;
  /** free text: what to skip */
  skip: string;
  /** any of these words present = worth a look (case-insensitive, code-side) */
  keywords: string[];
  /** any of these present = hard skip, no model call needed */
  blockers: string[];
  /** counties/cities he actually buys or hires in */
  areas: string[];
  /** deals only — a price outside this is a hard skip. 0 = no bound */
  minPrice: number;
  maxPrice: number;
  /** how he wants to sound */
  voice: string;
  /** question → the answer he wants given, every time */
  answers: { q: string; a: string }[];
  /** days before the single follow-up nudge */
  followUpDays: number;
}

export interface FbRules {
  deals: ModeRules;
  contractors: ModeRules;
}

/** Seeded from Tom's playbook + buy box (2026-07-26) so the page is useful
 * before he edits a word. Everything here is meant to be overwritten. */
export const DEFAULT_RULES: FbRules = {
  deals: {
    looksLike:
      "A single family house or 2-4 unit within about 3 hours of Orlando, needing work, where the seller sounds motivated: " +
      "inherited, estate, tired landlord, behind on payments, fire/water damage, hoarder, vacant, as-is, must sell fast. " +
      "Wholesaler posts with a real address and numbers count too.",
    skip:
      "Retail listings at full market price, rentals wanted, roommate posts, land with no structure, out of state, " +
      "timeshares, 'DM me for my list' with no property, and anything already under contract.",
    keywords: ["as-is", "as is", "fixer", "handyman", "tlc", "inherited", "estate", "cash only", "motivated", "must sell", "fsbo", "off market", "distressed", "vacant"],
    blockers: ["for rent", "roommate", "timeshare", "wanted to rent", "looking to rent", "hiring", "job opening"],
    areas: ["Orange", "Seminole", "Osceola", "Lake", "Volusia", "Marion", "Polk", "Brevard", "Hillsborough", "Pinellas", "Pasco", "Alachua", "Duval", "Sumter", "Flagler"],
    minPrice: 0,
    maxPrice: 400000,
    voice: "Plain, local, direct. Contractions. No hype, no emojis, no 'I'd love to connect'. Ask one clear question.",
    answers: [
      { q: "Are you an agent?", a: "No — I'm a buyer. I buy with my own cash, no commissions and no listing." },
      { q: "How fast can you close?", a: "Two to three weeks normally, and faster if title is clean. You pick the date." },
      { q: "Will you look at it as-is?", a: "Yes. I don't need it cleaned out or repaired — I buy exactly as it sits." },
      { q: "What's your offer?", a: "I'd rather see it or get photos first than throw a number at you. Send the address and I'll come look this week." },
    ],
    followUpDays: 4,
  },
  contractors: {
    looksLike:
      "A tradesperson posting their own finished work, or someone recommending one: roofing, HVAC, plumbing, electrical, " +
      "tile, flooring, drywall, paint, kitchen/bath remodel, general contractor. Central Florida. Photos of real jobs, " +
      "licensed and insured, responsive in comments.",
    skip:
      "Lead-gen and 'we connect you with pros' pages, out of area, no photos of their own work, anyone who won't give a " +
      "license number when asked, and franchises that only quote through a call center.",
    keywords: ["licensed", "insured", "free estimate", "before and after", "we install", "our crew", "finished this", "roofing", "hvac", "plumbing", "electrical", "tile", "flooring", "drywall", "remodel"],
    blockers: ["looking for work at", "need a job", "hiring drivers", "mlm", "crypto"],
    areas: ["Orlando", "Ocala", "Tampa", "Lakeland", "Daytona", "Melbourne", "Jacksonville", "Gainesville", "Sarasota", "Kissimmee"],
    minPrice: 0,
    maxPrice: 0,
    voice: "Respectful and specific — mention the actual job in their photos. He pays fast and has repeat work; say that plainly.",
    answers: [
      { q: "What's the job?", a: "Flip house, mostly cosmetic plus the systems that failed inspection. I'll send photos and a scope." },
      { q: "When do you need it?", a: "Starting within the next two to three weeks, and I work around your schedule if you're solid." },
      { q: "How do you pay?", a: "Draws on progress — a third to start, a third at the midpoint, the rest when it's done and lien waivers are signed." },
      { q: "Are you licensed?", a: "I ask every trade for a license number and a certificate of insurance before work starts. Nothing personal, it's every job." },
    ],
    followUpDays: 5,
  },
};

/* ------------------------------------------------------------ narrowing */

const str = (v: unknown, fb = ""): string => (typeof v === "string" ? v : fb);
const num = (v: unknown, fb = 0): number => {
  const n = typeof v === "number" ? v : typeof v === "string" ? parseFloat(v.replace(/[^0-9.]/g, "")) : NaN;
  return Number.isFinite(n) ? n : fb;
};
const list = (v: unknown, fb: string[]): string[] =>
  Array.isArray(v) ? v.map((x) => str(x).trim()).filter(Boolean) : fb;

function narrowMode(raw: unknown, fb: ModeRules): ModeRules {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return fb;
  const o = raw as Record<string, unknown>;
  const answers = Array.isArray(o["answers"])
    ? (o["answers"] as unknown[])
        .map((x) => (x && typeof x === "object" ? { q: str((x as Record<string, unknown>)["q"]), a: str((x as Record<string, unknown>)["a"]) } : { q: "", a: "" }))
        .filter((x) => x.q.trim() || x.a.trim())
    : fb.answers;
  return {
    looksLike: str(o["looksLike"], fb.looksLike),
    skip: str(o["skip"], fb.skip),
    keywords: list(o["keywords"], fb.keywords),
    blockers: list(o["blockers"], fb.blockers),
    areas: list(o["areas"], fb.areas),
    minPrice: num(o["minPrice"], fb.minPrice),
    maxPrice: num(o["maxPrice"], fb.maxPrice),
    voice: str(o["voice"], fb.voice),
    answers,
    followUpDays: Math.max(1, Math.round(num(o["followUpDays"], fb.followUpDays))),
  };
}

/** Read rules out of the org settings blob; any missing or junk piece falls
 * back to the default rather than blanking the page. */
export function narrowRules(automation: unknown): FbRules {
  const o = automation !== null && typeof automation === "object" && !Array.isArray(automation)
    ? (automation as Record<string, unknown>)["fbRules"]
    : null;
  const r = o !== null && typeof o === "object" && !Array.isArray(o) ? (o as Record<string, unknown>) : {};
  return {
    deals: narrowMode(r["deals"], DEFAULT_RULES.deals),
    contractors: narrowMode(r["contractors"], DEFAULT_RULES.contractors),
  };
}

/** Settings writes replace the whole key — so merge, never replace. */
export function automationWithRules(automation: unknown, rules: FbRules): Record<string, unknown> {
  const base = automation !== null && typeof automation === "object" && !Array.isArray(automation)
    ? { ...(automation as Record<string, unknown>) }
    : {};
  base["fbRules"] = rules as unknown as Record<string, unknown>;
  return base;
}

/** The rules, as instructions the model can follow. Kept short on purpose:
 * long rule lists send local reasoning models into minutes of deliberation. */
export function rulesPrompt(mode: FbMode, r: ModeRules): string {
  const out = [
    mode === "deals"
      ? "Tom buys houses to flip and hold as rentals within ~3 hours of Orlando."
      : "Tom is hiring trades for his flip projects in Central Florida.",
    "WORTH IT: " + r.looksLike,
    "SKIP: " + r.skip,
  ];
  if (r.areas.length) out.push("Areas he works: " + r.areas.join(", ") + ".");
  if (mode === "deals" && r.maxPrice > 0) out.push(`Price ceiling: $${r.maxPrice.toLocaleString("en-US")}.`);
  out.push("Voice: " + r.voice);
  return out.join("\n");
}
